import {useEffect, useMemo} from "react";
import {Checkbox, NativeSelect, Radio, Text, TextInput} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {dibAppApi} from "../../../dibAppApi/dibAppApi";
import {bffApi} from "../../chat/bffApi";
import {validFileTypes} from "./fileTypes";
import {getStoredTemplate} from "./templateStorage";
import type {DeliveryFormat, ExportDocument} from "./types";
import classes from "../checklist/modalStyles.module.css";

const rowClassNames = {
    root: classes.modalRow,
    labelWrapper: classes.modalRowLabelWrapper,
    label: classes.modalRowLabel,
};

type DocumentsListProps = {
    count: number;
    documents: ExportDocument[];
    setDocuments: (docs: ExportDocument[]) => void;
    areFilesAllowed?: boolean;
    setIsValid?: (valid: boolean) => void;
};

export const DocumentsList = ({
    count,
    documents,
    setDocuments,
    areFilesAllowed = true,
    setIsValid,
}: DocumentsListProps) => {
    const {t} = useTranslation();

    const handleToggle = (index: number) => {
        if (!documents?.length) return;
        const next = documents.slice();
        next[index] = {...next[index], selected: !next[index].selected};
        const selectedCount = next.filter((d) => d.selected).length;
        setIsValid?.(selectedCount > 0);
        setDocuments(next);
    };

    return (
        <div className={classes.modalSection}>
            <div className={classes.subheader}>
                {t("Title.TemplatesWithCount", {selectedDocsLength: count})}
            </div>
            {documents.length > 0 ? (
                <div className={classes.modalRowList}>
                    {documents.map((d, i) => {
                        const isFile =
                            !!d.resourceTypeId &&
                            validFileTypes.has(d.resourceTypeId);
                        const disabled = !areFilesAllowed && isFile;
                        return (
                            <Checkbox
                                key={d.id}
                                color="gneis.9"
                                disabled={disabled}
                                checked={disabled ? false : !!d.selected}
                                onChange={() => handleToggle(i)}
                                label={d.name}
                                classNames={rowClassNames}
                            />
                        );
                    })}
                </div>
            ) : (
                <Text size="sm" c="dimmed" className={classes.modalText}>
                    {t("Phrase.NoTemplatesChosen")}
                </Text>
            )}
        </div>
    );
};

type TemplateSelectProps = {
    topicId: string;
    selectedTemplate: string | undefined;
    onSelect: (id: string) => void;
    error?: boolean;
};

const PROMPT_ID = "-1";

export const TemplateSelect = ({
    topicId,
    selectedTemplate,
    onSelect,
    error,
}: TemplateSelectProps) => {
    const {t} = useTranslation();
    const {data: home, isLoading} = dibAppApi.endpoints.getHome.useQuery();
    const templates = home?.templates ?? [];

    const defaultTemplate = useMemo(
        () => ({id: "0", name: t("Label.DIBStandardTemplate")}),
        [t]
    );

    const available = useMemo(
        () => [defaultTemplate, ...templates],
        [defaultTemplate, templates]
    );

    // Waits for the template list, because the list decides the first choice. A
    // failed request gives an empty list and thus the default template.
    useEffect(() => {
        if (isLoading || selectedTemplate !== undefined) return;
        const stored = getStoredTemplate(
            topicId,
            available.map((tmpl) => tmpl.id)
        );
        onSelect(stored ?? defaultTemplate.id);
    }, [
        isLoading,
        available,
        defaultTemplate,
        selectedTemplate,
        onSelect,
        topicId,
    ]);

    if (available.length <= 1) return null;

    const options = [
        {value: PROMPT_ID, label: t("Phrase.ChooseStyleTemplate")},
        ...available.map((tmpl) => ({
            value: tmpl.id,
            label: tmpl.name,
        })),
    ];

    const value = selectedTemplate ?? PROMPT_ID;

    return (
        <NativeSelect
            label={t("Word.StyleTemplate")}
            data={options}
            value={value}
            onChange={(e) => {
                const v = e.currentTarget.value;
                if (v !== PROMPT_ID) onSelect(v);
            }}
            error={error ? t("Phrase.ChooseStyleTemplatePrompt") : undefined}
            classNames={{
                root: classes.selectRoot,
                label: classes.selectLabel,
            }}
        />
    );
};

type DeliveryControlsProps = {
    format: DeliveryFormat;
    onSetFormat: (f: DeliveryFormat) => void;
    recipients: string[];
    onSetRecipients: (r: string[]) => void;
};

export const DeliveryControls = ({
    format,
    onSetFormat,
    recipients,
    onSetRecipients,
}: DeliveryControlsProps) => {
    const {t} = useTranslation();
    const {data: user} = bffApi.endpoints.getUser.useQuery();
    const defaultEmail = user?.epost ?? "";

    return (
        <div className={classes.modalSection}>
            <div className={classes.subheader}>
                {t("Phrase.DeliveredAs")}
            </div>
            <Radio.Group
                value={format}
                onChange={(value) => onSetFormat(value as DeliveryFormat)}
            >
                <div className={classes.modalRowList}>
                    <Radio
                        value="doc"
                        label={t("Phrase.InAWordDocument")}
                        color="gneis.9"
                        classNames={rowClassNames}
                    />
                    <Radio
                        value="zip"
                        label={t("Word.ZipFile")}
                        color="gneis.9"
                        classNames={rowClassNames}
                    />
                    <Radio
                        value="email"
                        label={t("Phrase.AsEmailAttachment")}
                        color="gneis.9"
                        classNames={rowClassNames}
                    />
                </div>
            </Radio.Group>
            {format === "email" && (
                <TextInput
                    label={t("Word.Email")}
                    type="email"
                    defaultValue={recipients[0] ?? defaultEmail}
                    onBlur={(e) => onSetRecipients([e.currentTarget.value])}
                    classNames={{
                        root: classes.selectRoot,
                        label: classes.selectLabel,
                    }}
                />
            )}
        </div>
    );
};

type CommentsToggleProps = {
    withComments: boolean;
    setWithComments: (v: boolean) => void;
};

export const CommentsToggle = ({
    withComments,
    setWithComments,
}: CommentsToggleProps) => {
    const {t} = useTranslation();
    return (
        <Checkbox
            color="gneis.9"
            checked={withComments}
            onChange={(e) => setWithComments(e.currentTarget.checked)}
            label={t("Phrase.IncludeInstructions")}
            classNames={rowClassNames}
        />
    );
};
